import type {Dispatch, SetStateAction} from 'react';
import React, {useState} from 'react';

import {Button, Form, ListItem} from '@storybook/components';
import {styled} from '@storybook/theming';

import {format} from '../utils/githubHelpers';

import Comment from './Comment';
import {useAnswer} from './hooks/useAnswer';

import type {TransformedIssue} from '../utils/githubHelpers';

interface ComponentProps {
    issue: TransformedIssue;
    setUpdated: Dispatch<SetStateAction<number>>;
}

/**
 * Accordeon.
 *
 * @param props            Component props.
 * @param props.issue      The issue.
 * @param props.setUpdated The set updated.
 * @returns The component.
 */
const Accordeon = ({issue, setUpdated}: ComponentProps) => {
    const [open, setOpen] = useState(false);
    const {answer, handleComment, handleFinishedIssue, handleNewComment} = useAnswer({
        issueNumber: issue.number,
        setUpdated
    });

    /**
     * Handle open.
     *
     * @returns Nothing.
     */
    const handleOpen = () => setOpen(!open);

    return (
        <Wrapper>
            <ListItem
                key={issue.id}
                right={(
                    <RightBox>
                        <span>Updated at {format(issue.updatedAt)}</span>
                        <span>Comments({issue.commentNumber})</span>
                    </RightBox>
                )}
                title={(
                    <InfoBox>
                        <StateTag $isFixed={issue.state === 'closed'}>
                            {issue.state === 'closed' ? 'Fixed' : 'Open'}
                        </StateTag>
                        <IssueTitle>{issue.title}</IssueTitle>
                    </InfoBox>
                )}
                onClick={handleOpen}
            />
            <Body $isOpen={open}>
                <InnerBody>
                    <Comment avatar={issue.avatar} body={issue.body} date={issue.createdAt} type="issue" />
                    {issue.comments.map(comment => {
                        if (comment.type === 'error') {
                            return <Comment key={comment.id} body={comment.body} type="error" />;
                        }

                        return (
                            <Comment
                                key={comment.id}
                                avatar={comment.avatar}
                                body={comment.body}
                                date={comment.date}
                            />
                        );
                    })}
                    <CommentForm>
                        <CommentSpacer />
                        <CommentEdit>
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <Label htmlFor="body">
                                Add a comment
                                <Form.Textarea id="body" size="100%" value={answer} onChange={handleComment} />
                            </Label>
                            <ButtonWrapper>
                                <Button outline secondary small onClick={handleFinishedIssue}>
                                    Approve ticket
                                </Button>
                                <Button outline primary small onClick={handleNewComment}>
                                    Submit comment
                                </Button>
                            </ButtonWrapper>
                        </CommentEdit>
                    </CommentForm>
                </InnerBody>
            </Body>
            <Line />
        </Wrapper>
    );
};

Accordeon.displayName = 'Accordeon';

export default Accordeon;

const Wrapper = styled.div``;

const InfoBox = styled.div`
    align-items: center;
    display: flex;
    gap: 5px;
`;

interface StateTagProps {
    $isFixed: boolean;
}

const StateTag = styled.span<StateTagProps>`
    align-items: center;
    background-color: ${({$isFixed}) => ($isFixed ? 'rgba(255, 255, 212, 0.1)' : 'rgba(254, 222, 210, 0.1)')};
    border: 1px solid ${({$isFixed}) => ($isFixed ? 'rgba(102, 191, 60, 1)' : 'rgba(255, 68, 0, 1)')};
    border-radius: 15px;
    color: ${({$isFixed}) => ($isFixed ? 'rgba(102, 191, 60, 1)' : 'rgba(255, 68, 0, 1)')};
    display: flex;
    flex: 0 0 50px;
    font-size: 14px;
    font-weight: bold;
    height: 30px;
    justify-content: center;
    width: 50px;
`;

const IssueTitle = styled.span`
    flex: 2 0 auto;
    font-size: 16px;
    font-weight: bold;
    text-align: center;
`;

const RightBox = styled.div`
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    gap: 5px;
    justify-content: center;
`;

const Line = styled.div`
    border-bottom: 1px solid rgba(255,255,255,.1);
    height: 0;
    width: 100%;
`;

interface BodyProps {
    $isOpen: boolean;
}

const Body = styled.div<BodyProps>`
    display: grid;
    grid-template-rows: ${({$isOpen}) => ($isOpen ? '1fr' : '0fr')};
    overflow: hidden;
    transition: grid-template-rows 0.3s ease-in-out;
`;

const InnerBody = styled.div`
    min-height: 0;
`;

const CommentForm = styled.div`
    display: flex;
    gap: 10px;
    padding: 10px;
`;

const CommentSpacer = styled.div`
    flex: 0 0 40px;
`;

const CommentEdit = styled.div`
    width: 100%;
`;

const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
    margin-bottom: 5px;
    width: 100%;
`;

const ButtonWrapper = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;
`;